import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Collapse, IconButton, MenuItem, Paper, TextField, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';

import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import { biedOngedaanAan } from '../../utils/ongedaan';

/**
 * PL-uren in de tijd (Joris, 29 sep): de begrote uren die de PL zelf doet (PL-offerteregels),
 * min wat er al op geschreven is, per week verdeeld over start, draaidagen en oplevering —
 * t.o.v. de contracturen. Rood alleen bij overboeking. Uren zonder bruikbare datum staan
 * apart als "zonder datum", nooit geraden. Rekenregels: docs/pl-uren-planning.md.
 */
const API = `${BACKEND_URL}/Api/gripp/pl-uren-planning`;
const GRIJS = '#cbd5e1';
const TEKST_ZACHT = '#64748b';
const ACCENT = '#4f46e5';
const ROOD = '#dc2626';
const FASEN = [
  ['auto', 'volgt sectie'], ['doorlopend', 'doorlopend'], ['pre', 'preproductie'],
  ['productie', 'draaidagen'], ['post', 'postproductie'],
];
const FASE_LABEL = Object.fromEntries(FASEN);

const u = (n) => `${Math.round((n || 0) * 10) / 10}`.replace('.', ',');
const datum = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '—');

async function haalJson(url, opties) {
  const r = await authFetch(url, opties);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d?.detail || d?.error || `HTTP ${r.status}`);
  return d;
}

function WeekBalk({ week, capaciteit, schaal, actief, onClick }) {
  const hoogte = schaal > 0 ? Math.min(1, week.uren / schaal) : 0;
  const lijn = capaciteit != null && schaal > 0 ? Math.min(1, capaciteit / schaal) : null;
  const kleur = week.overboekt ? ROOD : (actief ? ACCENT : GRIJS);
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      aria-label={`${u(week.uren)} uur`}
      sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25,
        borderRadius: 1, py: 0.25, bgcolor: actief ? '#eef2ff' : 'transparent', '&:hover': { bgcolor: '#f8fafc' } }}
    >
      <Box sx={{ position: 'relative', height: 36, width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
        {lijn != null && (
          <Box sx={{ position: 'absolute', left: 2, right: 2, bottom: `${lijn * 100}%`, borderTop: '1px dashed #94a3b8' }} />
        )}
        <Box sx={{ width: '62%', height: `${hoogte * 100}%`, minHeight: week.uren > 0 ? 2 : 0, bgcolor: kleur, borderRadius: '3px 3px 0 0' }} />
      </Box>
      <Typography sx={{ fontSize: 10.5, lineHeight: 1, color: week.overboekt ? ROOD : TEKST_ZACHT,
        fontWeight: week.overboekt ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}>
        {week.uren > 0 ? u(week.uren) : '·'}
      </Typography>
    </Box>
  );
}

function ProjectRegel({ links, rechts, sub }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, py: 0.5, borderBottom: '1px solid #f1f5f9', alignItems: 'baseline' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13 }}>{links}</Typography>
        {sub && <Typography sx={{ fontSize: 11.5, color: TEKST_ZACHT }}>{sub}</Typography>}
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{rechts}</Typography>
    </Box>
  );
}

const projectNaam = (p) => `${p.nummer || ''} ${p.naam || ''}`.trim();

function WeekDetail({ pl, index, weken }) {
  const week = pl.weken[index];
  const w = weken[index];
  return (
    <Box>
      <Typography sx={{ fontSize: 12.5, color: TEKST_ZACHT, mb: 0.5 }}>
        Week {w?.week} (vanaf {datum(w?.start)}): {u(week.uren)} u
        {pl.capaciteit != null ? ` van ${u(pl.capaciteit)} u beschikbaar` : ' · contracturen onbekend'}
      </Typography>
      {week.projecten.length === 0
        ? <Typography sx={{ fontSize: 13, color: TEKST_ZACHT }}>Geen PL-uren ingepland.</Typography>
        : week.projecten.map((p) => (
          <ProjectRegel key={p.gripp_id} links={projectNaam(p)} sub={p.klant} rechts={`${u(p.uren)} u`} />
        ))}
    </Box>
  );
}

function NietDetail({ pl }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12.5, color: TEKST_ZACHT, mb: 0.5 }}>
        Resturen die niet in een week te zetten zijn — er ontbreekt een datum of die spreekt zich tegen. Er wordt niets geraden.
      </Typography>
      {pl.niet_in_te_plannen.map((n) => (
        <ProjectRegel key={`${n.gripp_id}-${n.fase}-${n.reden}`} links={projectNaam(n)}
          sub={`${FASE_LABEL[n.fase] || n.fase}: ${n.reden}`} rechts={`${u(n.uren)} u`} />
      ))}
      {pl.fase_voorbij.length > 0 && (
        <Typography sx={{ fontSize: 12, color: TEKST_ZACHT, mt: 1 }}>
          Daarnaast {u(pl.voorbij_totaal)} u restbudget in een fase die al voorbij is (draaidagen geweest) — telt niet als werkdruk.
        </Typography>
      )}
    </Box>
  );
}

function ProjectenDetail({ pl, kanWijzigen, onToggleRegel, bezig }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12.5, color: TEKST_ZACHT, mb: 0.5 }}>
        Projecten met PL-regels{kanWijzigen ? ' — vink een regel uit als de PL die niet zelf doet (bijv. regie door een ander)' : ''}.
      </Typography>
      {pl.projecten.map((p) => {
        const labels = [
          p.aandeel < 1 && `gedeeld (${Math.round(1 / p.aandeel)} PL's)`,
          !p.gekoppeld && 'niet gekoppeld aan Airtable',
          p.koppeling === 'offerte' && 'gekoppeld via geaccepteerde offerte',
          p.zonder_draaidagen && 'geen draaidagen in Airtable',
          p.start_na_draaidag && 'startdatum na draaidag',
          p.regelbron === 'airtable' && 'regels uit Airtable',
        ].filter(Boolean);
        return (
          <Box key={p.gripp_id} sx={{ py: 1, borderBottom: '1px solid #f1f5f9' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{projectNaam(p)}</Typography>
              <Typography sx={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>rest {u(p.rest)} u</Typography>
            </Box>
            <Typography sx={{ fontSize: 11.5, color: TEKST_ZACHT }}>
              start {datum(p.start)} · oplevering {datum(p.oplevering)} · draaidagen{' '}
              {p.draaidagen.length ? p.draaidagen.map(datum).join(', ') : '—'}
              {labels.length ? ` · ${labels.join(' · ')}` : ''}
            </Typography>
            {p.regels.map((r) => (
              <Box key={r.sleutel || `${r.code}-${r.product}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5 }}>
                <Checkbox size="small" checked={r.telt} disabled={!kanWijzigen || !r.sleutel || bezig}
                  onChange={() => onToggleRegel(p, r)} sx={{ p: 0.25 }}
                  inputProps={{ 'aria-label': `${r.product} telt voor PL` }} />
                <Typography sx={{ fontSize: 12, flex: 1, color: r.telt ? 'inherit' : TEKST_ZACHT,
                  textDecoration: r.telt ? 'none' : 'line-through' }}>
                  {r.product} <Box component="span" sx={{ color: TEKST_ZACHT }}>· {FASE_LABEL[r.fase] || r.fase}</Box>
                </Typography>
                <Typography sx={{ fontSize: 12, color: TEKST_ZACHT, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {u(r.begroot)} u begroot · {r.geschreven == null ? 'verbruik onbekend' : `${u(r.geschreven)} u geschreven`}
                </Typography>
              </Box>
            ))}
            {(p.opmerkingen || []).map((o) => (
              <Typography key={`${o.code}-${o.eenheid}`} sx={{ fontSize: 11.5, color: TEKST_ZACHT, pl: 4 }}>
                {o.product}: telt niet ({o.reden}: {o.eenheid || 'geen eenheid'})
              </Typography>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

function RegelsBeheer({ config, kanWijzigen, onOpslaan, bezig }) {
  const [nieuw, setNieuw] = useState({ code: '', naam: '', fase: 'auto' });
  const regels = config?.regels || [];
  // Elke wijziging als bewerking op de actuele config (heen/terug), zie opslaan().
  const metRegels = (fn) => (cfg) => ({ ...cfg, regels: fn(cfg.regels || []) });
  const zetFase = (code, fase) => metRegels((l) => l.map((x) => (x.code === code ? { ...x, fase } : x)));
  const verwijder = (code) => metRegels((l) => l.filter((x) => x.code !== code));
  const voegToe = (regel, index) => metRegels((l) => (l.some((x) => x.code === regel.code) ? l
    : [...l.slice(0, index), regel, ...l.slice(index)]));
  const codeOk = /^\d{4,6}$/.test(nieuw.code) && !regels.some((r) => r.code === nieuw.code);
  return (
    <Box>
      <Typography sx={{ fontSize: 12.5, color: TEKST_ZACHT, mb: 1 }}>
        Alleen regels met deze Gripp-productcodes tellen als PL-uren (het nummer tussen haakjes in de productnaam).
        Eenheid dag = 8 u, halve dag = 4 u. Fase "volgt sectie" kijkt naar de groepskop in de offerte.
      </Typography>
      {regels.map((r, i) => (
        <Box key={r.code} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
          <Typography sx={{ fontSize: 12.5, fontFamily: 'monospace', color: TEKST_ZACHT, width: 52 }}>{r.code}</Typography>
          <Typography sx={{ fontSize: 13, flex: 1 }}>{r.naam || '—'}</Typography>
          {kanWijzigen ? (
            <TextField select size="small" value={r.fase} disabled={bezig} variant="standard"
              onChange={(e) => onOpslaan(zetFase(r.code, e.target.value), zetFase(r.code, r.fase),
                `Fase van ${r.code} aangepast`)}
              sx={{ width: 130, '& .MuiInputBase-input': { fontSize: 12.5 } }}>
              {FASEN.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
            </TextField>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: TEKST_ZACHT, width: 130 }}>{FASE_LABEL[r.fase]}</Typography>
          )}
          {kanWijzigen && (
            <Tooltip title="Telt niet meer als PL-regel">
              <span>
                <IconButton size="small" disabled={bezig} onClick={() => onOpslaan(verwijder(r.code), voegToe(r, i), `${r.code} telt niet meer als PL-regel`)}
                  aria-label={`${r.code} verwijderen`}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      ))}
      {kanWijzigen && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mt: 1, flexWrap: 'wrap' }}>
          <TextField size="small" variant="standard" label="Code" value={nieuw.code} sx={{ width: 80 }}
            onChange={(e) => setNieuw({ ...nieuw, code: e.target.value.trim() })} />
          <TextField size="small" variant="standard" label="Omschrijving" value={nieuw.naam} sx={{ flex: '1 1 160px' }}
            onChange={(e) => setNieuw({ ...nieuw, naam: e.target.value })} />
          <TextField select size="small" variant="standard" label="Fase" value={nieuw.fase} sx={{ width: 130 }}
            onChange={(e) => setNieuw({ ...nieuw, fase: e.target.value })}>
            {FASEN.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </TextField>
          <Button size="small" disabled={!codeOk || bezig}
            onClick={() => {
              onOpslaan(voegToe(nieuw, regels.length), verwijder(nieuw.code), `${nieuw.code} telt als PL-regel`);
              setNieuw({ code: '', naam: '', fase: 'auto' });
            }}>
            Toevoegen
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default function PLUrenBlok({ refreshKey, kanWijzigen }) {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [open, setOpen] = useState(true);
  const [keuze, setKeuze] = useState(null); // { pl, soort: 'week'|'niet'|'projecten', index }
  const [regelsOpen, setRegelsOpen] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [versie, setVersie] = useState(0);

  useEffect(() => {
    let actief = true;
    setFout(null);
    haalJson(`${API}?weken=12`)
      .then((d) => { if (actief) setData(d); })
      .catch((e) => { if (actief) setFout(e.message); })
      // Bediening pas weer vrij als de herberekende planning binnen is.
      .finally(() => { if (actief) setBezig(false); });
    return () => { actief = false; };
  }, [refreshKey, versie]);

  // Actuele config voor wijzigingen en undo: elke actie is een bewerking (heen/terug) op de
  // config van dát moment, zodat 'Ongedaan maken' alleen die ene wijziging terugdraait en
  // latere wijzigingen laat staan (Codex code-review r2).
  const configRef = useRef(null);
  configRef.current = data?.config || null;

  // Alle schrijfacties (opslaan én ongedaan maken) lopen achter elkaar: elke stap leest de
  // config pas als hij aan de beurt is, dus een undo kan een lopende wijziging niet wissen
  // (Codex code-review r3).
  const rijRef = useRef(Promise.resolve());

  const opslaan = useCallback((heen, terug, melding) => {
    const verwerk = (r) => {
      // Nieuwe config meteen overnemen, zodat een volgende wijziging daarop voortbouwt;
      // de bediening blijft dicht tot de herberekende planning er is (effect hierboven).
      if (r?.config) configRef.current = r.config;
      setData((d) => (d ? { ...d, config: r.config || d.config } : d));
      setBezig(true);
      setVersie((v) => v + 1);
    };
    const inRij = (bewerk) => {
      const stap = rijRef.current.catch(() => {}).then(async () => {
        const huidig = configRef.current;
        if (!huidig) throw new Error('geen instellingen geladen');
        const c = bewerk(huidig);
        verwerk(await haalJson(`${API}/config`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ regels: c.regels, uitzonderingen: c.uitzonderingen || [], beschikbaar_pct: c.beschikbaar_pct }),
        }));
      });
      rijRef.current = stap;
      return stap;
    };
    setBezig(true);
    return inRij(heen)
      .then(() => { biedOngedaanAan(melding, () => inRij(terug), { eigenKnop: true }); })
      .catch((e) => { toast.error(`Opslaan mislukt: ${e.message}`); setBezig(false); });
  }, []);

  const toggleRegel = useCallback((project, regel) => {
    if (!regel.sleutel) return;
    const is = (x) => x.gripp_id === project.gripp_id && x.regel === regel.sleutel;
    const zetUit = (uit) => (cfg) => {
      const lijst = (cfg.uitzonderingen || []).filter((x) => !is(x));
      return { ...cfg, uitzonderingen: uit ? [...lijst, { gripp_id: project.gripp_id, regel: regel.sleutel }] : lijst };
    };
    const wasUit = !regel.telt;
    opslaan(zetUit(!wasUit), zetUit(wasUit),
      `${regel.product} ${regel.telt ? 'telt niet meer' : 'telt weer'} voor ${project.nummer}`);
  }, [opslaan]);

  // Alle PL's met PL-regels blijven zichtbaar — ook met alleen 'later'/'fase voorbij' of met
  // alles uitgezet, anders is een uitzondering niet meer terug te zetten (Codex code-review r1).
  const pls = useMemo(() => data?.pls || [], [data]);
  const kies = (pl, soort, index = null) => setKeuze((k) => (
    k && k.pl === pl && k.soort === soort && k.index === index ? null : { pl, soort, index }));

  if (fout) {
    return (
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid #e5e7eb' }} elevation={0}>
        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>PL-uren · komende 12 weken</Typography>
        <Typography sx={{ fontSize: 13, color: TEKST_ZACHT }}>Kon de PL-uren niet laden ({fout}).</Typography>
      </Paper>
    );
  }
  if (!data) {
    return (
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center' }} elevation={0}>
        <CircularProgress size={20} />
      </Paper>
    );
  }

  const weken = data.weken || [];
  const d = data.dekking || {};
  const kolommen = `minmax(120px, 170px) 64px repeat(${weken.length}, minmax(34px, 1fr)) 104px`;

  return (
    <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: 3, border: '1px solid #e5e7eb' }} elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', cursor: 'pointer' }}
        onClick={() => setOpen(!open)}>
        {open ? <ExpandLessIcon sx={{ color: TEKST_ZACHT, alignSelf: 'center' }} /> : <ExpandMoreIcon sx={{ color: TEKST_ZACHT, alignSelf: 'center' }} />}
        <Typography sx={{ fontWeight: 700, fontSize: 16, flex: 1 }}>PL-uren · komende 12 weken</Typography>
        <Typography sx={{ fontSize: 12, color: TEKST_ZACHT }}>
          begrote PL-uren uit de offerte, min al geschreven · per week t.o.v. contracturen
        </Typography>
      </Box>
      <Collapse in={open}>
        <Typography sx={{ fontSize: 12, color: TEKST_ZACHT, mt: 1, mb: 1.5 }}>
          {d.met_pl_uren ?? 0} van {d.opdrachten ?? 0} opdrachten hebben PL-regels: {d.helemaal_ingepland ?? 0} helemaal
          ingepland, {d.deels_ingepland ?? 0} deels, {d.niet_ingepland ?? 0} niet (datum ontbreekt), {d.geen_rest ?? 0} zonder
          resturen.{data.uren_spiegel_tot ? ` Geschreven uren t/m ${datum(String(data.uren_spiegel_tot).slice(0, 10))}.` : ''}
        </Typography>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 760 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: kolommen, gap: 0.5, alignItems: 'end', pb: 0.5,
              borderBottom: '1px solid #e5e7eb' }}>
              <Typography sx={{ fontSize: 11, color: TEKST_ZACHT }}>Projectleider</Typography>
              <Typography sx={{ fontSize: 11, color: TEKST_ZACHT }}>per week</Typography>
              {weken.map((w, i) => (
                <Tooltip key={w.start} title={`vanaf ${datum(w.start)}`}>
                  <Typography sx={{ fontSize: 11, color: i === 0 ? ACCENT : TEKST_ZACHT, textAlign: 'center',
                    fontWeight: i === 0 ? 700 : 400 }}>
                    wk {w.week}
                  </Typography>
                </Tooltip>
              ))}
              <Typography sx={{ fontSize: 11, color: TEKST_ZACHT, textAlign: 'right' }}>zonder datum</Typography>
            </Box>

            {pls.length === 0 && (
              <Typography sx={{ fontSize: 13, color: TEKST_ZACHT, py: 2 }}>Geen PL-uren gevonden.</Typography>
            )}
            {pls.map((pl) => {
              const sleutel = pl.employee_id || pl.naam;
              const maxWeek = Math.max(0, ...pl.weken.map((w) => w.uren));
              const schaal = Math.max(maxWeek, (pl.capaciteit ?? 0) * 1.25, 1);
              const gekozen = keuze && keuze.pl === sleutel ? keuze : null;
              return (
                <Box key={sleutel} sx={{ borderBottom: '1px solid #f1f5f9' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: kolommen, gap: 0.5, alignItems: 'end', py: 0.75 }}>
                    <Typography role="button" tabIndex={0} onClick={() => kies(sleutel, 'projecten')}
                      onKeyDown={(e) => { if (e.key === 'Enter') kies(sleutel, 'projecten'); }}
                      sx={{ fontSize: 13.5, fontWeight: 600, cursor: 'pointer', alignSelf: 'center',
                        color: gekozen?.soort === 'projecten' ? ACCENT : 'inherit', '&:hover': { color: ACCENT } }}>
                      {pl.naam}
                      {pl.weken_overboekt > 0 && (
                        <Box component="span" sx={{ color: ROOD, fontWeight: 700, fontSize: 11.5, ml: 0.75 }}>
                          {pl.weken_overboekt}× over
                        </Box>
                      )}
                    </Typography>
                    <Tooltip title={pl.capaciteit == null ? 'Geen contracturen bekend (Admin → gebruikers)' : `${u(pl.contracturen)} contracturen`}>
                      <Typography sx={{ fontSize: 12, color: TEKST_ZACHT, alignSelf: 'center', fontVariantNumeric: 'tabular-nums' }}>
                        {pl.capaciteit == null ? '? u' : `${u(pl.capaciteit)} u`}
                      </Typography>
                    </Tooltip>
                    {pl.weken.map((w, i) => (
                      <WeekBalk key={weken[i]?.start || i} week={w} capaciteit={pl.capaciteit} schaal={schaal}
                        actief={gekozen?.soort === 'week' && gekozen.index === i}
                        onClick={() => kies(sleutel, 'week', i)} />
                    ))}
                    <Typography role="button" tabIndex={0} onClick={() => kies(sleutel, 'niet')}
                      onKeyDown={(e) => { if (e.key === 'Enter') kies(sleutel, 'niet'); }}
                      sx={{ fontSize: 12, color: gekozen?.soort === 'niet' ? ACCENT : TEKST_ZACHT, textAlign: 'right',
                        alignSelf: 'center', cursor: 'pointer', fontVariantNumeric: 'tabular-nums', '&:hover': { color: ACCENT } }}>
                      {pl.niet_totaal > 0 ? `${u(pl.niet_totaal)} u` : '—'}
                      {pl.later > 0 && (
                        <Box component="span" sx={{ display: 'block', fontSize: 10.5 }}>+{u(pl.later)} u later</Box>
                      )}
                    </Typography>
                  </Box>
                  <Collapse in={!!gekozen} unmountOnExit>
                    <Box sx={{ pl: { xs: 1, md: 2 }, pr: 1, pb: 1.5, pt: 0.5, bgcolor: '#fafafa', borderRadius: 2, mb: 1 }}>
                      {gekozen?.soort === 'week' && <WeekDetail pl={pl} index={gekozen.index} weken={weken} />}
                      {gekozen?.soort === 'niet' && <NietDetail pl={pl} />}
                      {gekozen?.soort === 'projecten' && (
                        <ProjectenDetail pl={pl} kanWijzigen={kanWijzigen} onToggleRegel={toggleRegel} bezig={bezig} />
                      )}
                      {pl.onbekend_verbruik > 0 && (
                        <Typography sx={{ fontSize: 11.5, color: TEKST_ZACHT, mt: 1 }}>
                          Waarvan {u(pl.onbekend_verbruik)} u met onbekend verbruik (volle begroting geteld).
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ mt: 1.5 }}>
          <Typography role="button" tabIndex={0} onClick={() => setRegelsOpen(!regelsOpen)}
            onKeyDown={(e) => { if (e.key === 'Enter') setRegelsOpen(!regelsOpen); }}
            sx={{ fontSize: 12.5, color: TEKST_ZACHT, cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
              '&:hover': { color: ACCENT } }}>
            {regelsOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
            Welke regels tellen ({(data.config?.regels || []).length})
          </Typography>
          <Collapse in={regelsOpen} unmountOnExit>
            <Box sx={{ pt: 1 }}>
              <RegelsBeheer config={data.config} kanWijzigen={kanWijzigen} onOpslaan={opslaan} bezig={bezig} />
            </Box>
          </Collapse>
        </Box>
      </Collapse>
    </Paper>
  );
}
